import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, Clock, Shield, Activity, RefreshCw, X, ChevronRight, ExternalLink } from 'lucide-react';

const WS_URL = process.env.REACT_APP_WS_URL || 'wss://1r5x85vspl.execute-api.us-west-2.amazonaws.com/production';
const SNOW_URL = 'https://dish.service-now.com';

const HealthDot = ({ status }) => {
  const colors = { healthy: 'bg-emerald-400', degraded: 'bg-amber-400', critical: 'bg-red-400 animate-pulse' };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[status] || 'bg-slate-500'}`} />;
};

const Skeleton = ({ className }) => (
  <div className={`bg-surface-2 rounded animate-pulse ${className}`} />
);

// Full dashboard skeleton shown on initial load
const DashboardSkeleton = () => (
  <div className="p-6 max-w-7xl mx-auto">
    {/* Centered loading indicator */}
    <div className="flex flex-col items-center justify-center py-12 mb-8">
      <div className="relative mb-4">
        <div className="w-10 h-10 border-2 border-border rounded-full" />
        <div className="absolute inset-0 w-10 h-10 border-2 border-t-blue-400 rounded-full animate-spin" />
      </div>
      <p className="text-sm text-text-secondary font-medium">Loading dashboard...</p>
      <p className="text-xs text-text-muted mt-1">Fetching data from ServiceNow & Dynatrace</p>
    </div>

    {/* Ghost layout */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="rounded-card border border-border bg-surface p-4">
          <div className="h-3 w-24 bg-surface-2 rounded mb-3" />
          <div className="h-8 w-14 bg-surface-2 rounded" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-surface border border-border rounded-card p-5">
        <div className="h-4 w-36 bg-surface-2 rounded mb-5" />
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-8 w-full bg-surface-2 rounded opacity-60" style={{ opacity: 1 - i * 0.12 }} />
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <div className="bg-surface border border-border rounded-card p-5">
          <div className="h-4 w-28 bg-surface-2 rounded mb-4" />
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-5 w-full bg-surface-2 rounded" style={{ opacity: 1 - i * 0.15 }} />
            ))}
          </div>
        </div>
        <div className="bg-surface border border-border rounded-card p-5">
          <div className="h-4 w-32 bg-surface-2 rounded mb-4" />
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-5 w-full bg-surface-2 rounded" style={{ opacity: 1 - i * 0.15 }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);
  const [detailPanel, setDetailPanel] = useState(null);
  const detailRef = useRef(null);
  const [showMonitoredOnly, setShowMonitoredOnly] = useState(false); // 'incidents' | 'changes' | 'cis' | 'degraded'

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    try {
      const ws = new WebSocket(WS_URL);
      ws.onopen = () => ws.send(JSON.stringify({ action: 'ci_health' }));
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.ci_health || msg.summary || msg.todays_changes) {
            setData(msg);
            setLastRefresh(new Date());
          }
        } catch (e) { console.error('Dashboard parse error:', e); }
        ws.close();
        setLoading(false);
      };
      ws.onerror = () => { setError('Failed to connect'); setLoading(false); };
      ws.onclose = () => setLoading(false);
      setTimeout(() => { try { ws.close(); } catch(e) {} setLoading(false); }, 15000);
    } catch (e) { setError(e.message); setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const i = setInterval(fetchData, 300000); return () => clearInterval(i); }, [fetchData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'r' || e.key === 'R') { e.preventDefault(); fetchData(); }
      if (e.key === 'Escape') setDetailPanel(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [fetchData]);

  // Auto-scroll to detail panel
  useEffect(() => {
    if (detailPanel && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [detailPanel]);

  const summary = data?.summary || {};
  const ciHealthAll = data?.ci_health || [];
  const ciHealth = showMonitoredOnly ? ciHealthAll.filter(ci => ci.has_mapping) : ciHealthAll;
  const todaysChanges = data?.todays_changes || {};
  const incidents = data?.open_incidents || [];

  // Show full skeleton on initial load (no data yet)
  if (loading && !data) return <DashboardSkeleton />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[20px] font-semibold text-text-primary">Command Center</h1>
          <p className="text-xs text-text-muted">IT Operations Overview</p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && <span className="text-xs text-text-muted">Updated {lastRefresh.toLocaleTimeString()}</span>}
          <button onClick={fetchData} disabled={loading} className="p-2 rounded-card bg-surface-2 border border-border-2 text-text-secondary hover:bg-surface-2 transition-colors disabled:opacity-50" aria-label="Refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Alert Banner — clickable cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <AlertCard icon={<AlertTriangle size={16} />} label="Sev 1/2 Incidents" value={loading ? null : incidents.length}
          color={incidents.length > 0 ? 'red' : 'green'} loading={loading}
          active={detailPanel === 'incidents'} onClick={() => setDetailPanel(detailPanel === 'incidents' ? null : 'incidents')} />
        <AlertCard icon={<Clock size={16} />} label="Changes Today" value={loading ? null : todaysChanges.total}
          color="blue" loading={loading}
          active={detailPanel === 'changes'} onClick={() => setDetailPanel(detailPanel === 'changes' ? null : 'changes')} />
        <AlertCard icon={<Shield size={16} />} label="CIs Tracked" value={loading ? null : summary.total_cis_tracked}
          color="slate" loading={loading}
          active={detailPanel === 'cis'} onClick={() => setDetailPanel(detailPanel === 'cis' ? null : 'cis')} />
        <AlertCard icon={<Activity size={16} />} label="Degraded / Critical" value={loading ? null : `${summary.degraded || 0} / ${summary.critical || 0}`}
          color={(summary.critical > 0) ? 'red' : (summary.degraded > 0) ? 'amber' : 'green'} loading={loading}
          active={detailPanel === 'degraded'} onClick={() => setDetailPanel(detailPanel === 'degraded' ? null : 'degraded')} />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* CI Health Tracker */}
        <div className="lg:col-span-2 bg-surface border border-border rounded-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[13px] font-medium text-text-primary">Impacted CI Health</h2>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowMonitoredOnly(!showMonitoredOnly)} className="text-[11px] px-2 py-0.5 rounded border border-border text-text-muted hover:text-text-primary hover:border-border-2 transition-colors">
                {showMonitoredOnly ? '✓ Monitored Only' : 'Show All'}
              </button>
              <span className="text-[11px] text-text-muted">{ciHealth.length} CIs</span>
            </div>
          </div>
          {loading ? (
            <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
          ) : ciHealth.length === 0 ? (
            <p className="text-sm text-text-muted py-4">No impacted CIs found in recent changes.</p>
          ) : (
            <div className="space-y-1 max-h-[360px] overflow-y-auto">
              <div className="grid grid-cols-12 gap-2 text-xs text-text-muted pb-1 border-b border-border sticky top-0 bg-surface">
                <span className="col-span-1">Status</span>
                <span className="col-span-3">CI Name</span>
                <span className="col-span-3">Change</span>
                <span className="col-span-2">Source</span>
                <span className="col-span-3">Monitoring</span>
              </div>
              {ciHealth.map((ci, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center py-1.5 text-sm border-b border-border/50 hover:bg-surface-2/30">
                  <span className="col-span-1"><HealthDot status={ci.health} /></span>
                  <a href={`${SNOW_URL}/now/nav/ui/classic/params/target/cmdb_ci.do%3Fsys_id%3D${ci.ci_sys_id}`} target="_blank" rel="noopener noreferrer" className="col-span-3 text-text-primary truncate hover:text-blue-400" title={ci.ci_name}>{ci.ci_name}</a>
                  <a href={`${SNOW_URL}/now/nav/ui/classic/params/target/change_request.do%3Fsysparm_query%3Dnumber%3D${ci.change_number}`} target="_blank" rel="noopener noreferrer" className="col-span-3 text-blue-400 hover:text-blue-400 font-mono text-xs">{ci.change_number}</a>
                  <span className={`col-span-2 text-xs ${ci.source === "discovered" ? "text-amber-400" : "text-text-muted"}`}>{ci.source === "primary" ? "Primary CI" : ci.source === "discovered" ? "Discovered" : "Attached"}</span>
                  <span className={`col-span-2 text-xs flex items-center gap-1 ${ci.problems_count > 0 ? 'text-red-400' : ci.has_mapping ? 'text-emerald-400' : 'text-text-muted'}`}>
                    {ci.problems_count > 0 ? `${ci.problems_count} problem${ci.problems_count > 1 ? 's' : ''}` : ci.has_mapping && ci.entity_id ? (
                      <a href={`https://cwv21753.live.dynatrace.com/ui/entity/${ci.entity_id}`} target="_blank" rel="noopener noreferrer" className="px-2 py-0.5 text-[10px] font-medium bg-blue-500/15 text-blue-400 border border-blue-500/30 rounded hover:bg-blue-500/25 transition-colors whitespace-nowrap">
                        View Logs
                      </a>
                    ) : 'No Dynatrace mapping'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <div className="bg-surface border border-border rounded-card p-4 cursor-pointer hover:border-border-2 transition-colors"
               onClick={() => setDetailPanel(detailPanel === 'changes' ? null : 'changes')}>
            <div className="flex items-center justify-between mb-3"><h2 className="text-[13px] font-medium text-text-primary">Today's Changes</h2><ChevronRight size={14} className="text-text-muted" /></div>
            {loading ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Total</span>
                  <span className="text-text-primary font-medium">{todaysChanges.total || 0}</span>
                </div>
                {Object.entries(todaysChanges.by_type || {}).map(([type, count]) => (
                  <div key={type} className="flex justify-between text-sm">
                    <span className="text-text-secondary">{type}</span>
                    <span className={`font-medium ${type === 'Emergency' ? 'text-red-400' : 'text-text-primary'}`}>{count}</span>
                  </div>
                ))}
                {Object.keys(todaysChanges.by_type || {}).length === 0 && <p className="text-xs text-text-muted">No changes scheduled today.</p>}
              </div>
            )}
          </div>

          <div className="bg-surface border border-border rounded-card p-4 cursor-pointer hover:border-border-2 transition-colors"
               onClick={() => setDetailPanel(detailPanel === 'incidents' ? null : 'incidents')}>
            <div className="flex items-center justify-between mb-3"><h2 className="text-[13px] font-medium text-text-primary">Open Sev 1/2 Incidents</h2><ChevronRight size={14} className="text-text-muted" /></div>
            {loading ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
            ) : incidents.length === 0 ? (
              <p className="text-sm text-emerald-400">✓ No Sev 1/2 incidents</p>
            ) : (
              <div className="space-y-2">
                {incidents.map((inc, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${inc.severity?.includes('1') ? 'bg-negative/20 text-red-400' : 'bg-surface-2 text-amber-400'}`}>
                      S{inc.severity?.match(/\d/)?.[0] || '?'}
                    </span>
                    <div className="min-w-0">
                      <a href={`${SNOW_URL}/now/nav/ui/classic/params/target/incident.do%3Fsys_id%3D${inc.sys_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-400 font-mono text-xs">{inc.number}</a>
                      <p className="text-text-secondary text-xs truncate">{inc.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Panel — opens when a card/section is clicked */}
      {detailPanel && (
        <div ref={detailRef}><DetailTable panel={detailPanel} data={data} onClose={() => setDetailPanel(null)} />
        </div>)}

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-card p-3 text-sm text-red-400">{error}</div>}
    </div>
  );
};

// Detail table that opens at the bottom
const DetailTable = ({ panel, data, onClose }) => {
  const titles = { incidents: 'Open Sev 1/2 Incidents', changes: "Today's Changes", cis: 'Tracked CIs (Last 7 Days)', degraded: 'Degraded & Critical CIs' };
  const ciHealth = data?.ci_health || [];
  const incidents = data?.open_incidents || [];
  const changes = data?.todays_changes || {};

  let rows;
  let columns;

  if (panel === 'incidents') {
    columns = ['Number', 'Severity', 'Description', 'State'];
    rows = incidents.map(i => [{value: i.number, href: `${SNOW_URL}/now/nav/ui/classic/params/target/incident.do%3Fsys_id%3D${i.sys_id}`}, i.severity || '—', i.description, i.state]);
  } else if (panel === 'changes') {
    columns = ['Number', 'Description', 'Type', 'State', 'Risk'];
    const records = changes.records || [];
    if (records.length > 0) {
      rows = records.map(c => [
        {value: c.number, href: `${SNOW_URL}/now/nav/ui/classic/params/target/change_request.do%3Fsysparm_query%3Dnumber%3D${c.number}`},
        c.description,
        c.type || '—',
        c.state || '—',
        c.risk || '—'
      ]);
    } else {
      rows = Object.entries(changes.by_type || {}).map(([t, c]) => ['—', '—', t, '—', c]);
    }
  } else if (panel === 'cis') {
    columns = ['CI Name', 'Change', 'Type', 'State', 'Monitoring', 'Health'];
    rows = ciHealth.map(ci => [{value: ci.ci_name, href: `${SNOW_URL}/now/nav/ui/classic/params/target/cmdb_ci.do%3Fsys_id%3D${ci.ci_sys_id}`}, {value: ci.change_number, href: `${SNOW_URL}/now/nav/ui/classic/params/target/change_request.do%3Fsysparm_query%3Dnumber%3D${ci.change_number}`}, ci.change_type || '—', ci.change_state || '—', ci.has_mapping ? '✓ Mapped' : 'No mapping', ci.health]);
  } else if (panel === 'degraded') {
    const filtered = ciHealth.filter(ci => ci.health !== 'healthy');
    columns = ['CI Name', 'Change', 'Health', 'Problems'];
    rows = filtered.length > 0
      ? filtered.map(ci => [ci.ci_name, ci.change_number, ci.health, ci.problems?.map(p => p.title).join(', ') || '—'])
      : [['All CIs are healthy', '', '', '']];
  }

  return (
    <div className="bg-surface-2 border border-border rounded-card p-4 mb-6 animate-[fadeIn_0.2s_ease-in]">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-text-primary">{titles[panel]}</h2>
        <button onClick={onClose} className="p-1 rounded hover:bg-surface-2 text-text-secondary hover:text-text-primary" aria-label="Close">
          <X size={16} />
        </button>
      </div>
      <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              {columns.map((col, i) => (
                <th key={i} className="text-left py-2 px-3 text-xs font-medium text-text-muted">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-border/50 hover:bg-surface-2/30">
                {row.map((cell, j) => (
                  <td key={j} className={`py-2 px-3 ${j === 0 ? 'text-text-primary' : 'text-text-secondary'} ${
                    (cell === 'critical' || cell?.value === 'critical') ? 'text-red-400' : (cell === 'degraded' || cell?.value === 'degraded') ? 'text-amber-400' : ''
                  }`}>
                    {cell?.href ? <a href={cell.href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-400">{cell.value}</a> : (cell?.value || cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Alert card — now clickable
const AlertCard = ({ icon, label, value, color, loading, active, onClick }) => {
  const colorMap = {
    red: 'border-red-500/20 bg-surface-2',
    amber: 'border-amber-500/20 bg-surface-2',
    green: 'border-emerald-500/20 bg-surface-2',
    blue: 'border-blue-500/20 bg-blue-500/10',
    slate: 'border-border bg-surface-2',
  };
  const textColor = {
    red: 'text-red-400', amber: 'text-amber-400', green: 'text-emerald-400', blue: 'text-blue-400', slate: 'text-text-primary',
  };

  return (
    <div
      onClick={onClick}
      className={`rounded-card border p-3 cursor-pointer transition-all ${colorMap[color] || colorMap.slate} ${active ? 'ring-1 ring-slate-500 scale-[1.02]' : 'hover:brightness-110'}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={textColor[color] || 'text-text-secondary'}>{icon}</span>
        <span className="text-xs text-text-secondary">{label}</span>
      </div>
      {loading ? <Skeleton className="h-6 w-16" /> : (
        <span className={`text-lg font-semibold ${textColor[color] || 'text-text-primary'}`}>{value}</span>
      )}
    </div>
  );
};

export default Dashboard;
